import Container from '@/components/Container';
import DefaultContentTemplate from '@/components/DefaultContentTemplate';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import { RelatedContent } from '@/components/DefaultContentTemplate/RelatedContent';
import Divider from '@/components/Divider';
import { Enum_Favorite_Type, PodcastEntity, PodcastListeningTimeEntity, UploadFile } from '@/graphql/generated/graphql';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
  createPodcastListeningTime,
  fetchPodcast,
  fetchRelatedContent,
  findListeningTime,
  updatePodcastListeningTime,
} from '@/services/podcast/podcast.service';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import isPodcastDone from '../utils/is-done';
import TitleWithActions from '@/components/TitleWithActions';
import LikeAndFavorite from '@/components/LikeAndFavorite';
import { trackPodcastPlayed } from '@/lib/ga-custom-events/podcast';
import PageHeader from '@/components/PageHeader';

export default function PodcastPage() {
  const [podcast, setPodcast] = useState<PodcastEntity | null>(null);
  const [relatedContent, setRelatedContent] = useState<RelatedContent[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const lastSentTimeRef = useRef<PodcastListeningTimeEntity>({
    id: null,
    attributes: { listeningTime: 0, duration: 0 },
  }); // Ref to store last sent time

  const { slug } = useParams();
  const { authentication } = useAuthentication();

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);

      const [podcastResult, relatedContentResult] = await Promise.all([fetchPodcast(slug), fetchRelatedContent(slug)]);
      const listeningTimeResult = await findListeningTime(podcastResult?.podcast?.id || '');
      const { isDone } = isPodcastDone(podcastResult.podcast!);
      if (listeningTimeResult.listeningTime) {
        lastSentTimeRef.current = listeningTimeResult.listeningTime;
        if (isDone) {
          const prevAttributes = lastSentTimeRef.current.attributes;

          lastSentTimeRef.current = {
            ...lastSentTimeRef.current,
            attributes: {
              ...prevAttributes,
              listeningTime: 0,
              duration: prevAttributes?.duration,
            },
          };
        }
      }

      setPodcast(podcastResult?.podcast);
      setRelatedContent(relatedContentResult.podcasts || []);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleTimeUpdate = async (audio: HTMLAudioElement) => {
    const currentTime = Math.floor(audio.currentTime);
    const duration = Math.floor(audio.duration);
    if (!lastSentTimeRef.current.id) {
      const { data, error } = await createPodcastListeningTime({
        duration: duration,
        listeningTime: currentTime,
        podcast: podcast?.id || '',
        user: authentication.user.id,
      });

      if (error) {
        console.error('Error creating podcast listening time', error);
        return;
      }

      if (data) {
        lastSentTimeRef.current = data.createPodcastListeningTime?.data as PodcastListeningTimeEntity;
      }
      return;
    }

    await updatePodcastListeningTime(lastSentTimeRef.current.id, {
      listeningTime: currentTime,
    });

    return;
  };

  const renderPodcastHeader = () => {
    return (
      <>
        <DefaultContentTemplateMedia
          className="!my-sh-15 !mb-sh-15"
          component={{
            id: '1',
            media: {
              data: {
                attributes: {
                  url: podcast?.attributes?.cover.data?.attributes?.url || '',
                  alternativeText: podcast?.attributes?.cover.data?.attributes?.alternativeText || '',
                } as UploadFile,
              },
            },
          }}
        />

        {podcast?.id && (
          <div className="!mb-sh-15 flex items-center gap-sh-15">
            <LikeAndFavorite entity={podcast} type={Enum_Favorite_Type.Podcast} entityId={podcast!.id} />
          </div>
        )}

        <div className="mt-sh-15">
          {podcast?.id ? (
            <TitleWithActions
              title={podcast!.attributes!.title}
              entity={podcast}
              like={false}
              favorite={false}
              type={Enum_Favorite_Type.Podcast}
              entityId={podcast!.id}
            />
          ) : null}
        </div>

        <Divider />
        <p className=" my-sh-30 text-sh-xxs">{podcast?.attributes?.subHeader}</p>
      </>
    );
  };

  return (
    <>
      <Container className="min-h-40">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <PageHeader title="Podcast" url="/podcast" type="none" />
          {!loading && renderPodcastHeader()}
          <DefaultContentTemplate
            loading={loading}
            listeningTime={lastSentTimeRef.current}
            handleTimeUpdate={handleTimeUpdate}
            content={podcast?.attributes?.DefaultContentTemplate}
            tags={podcast?.attributes?.themes?.data.map((theme) => theme?.attributes?.theme?.toString() || '') || []}
            relatedContent={relatedContent || []}
            onPlayAudio={() => {
              if (!podcast) return;
              trackPodcastPlayed(podcast);
            }}
          />
        </div>
      </Container>
    </>
  );
}
